import React from 'react';

/**
 * ClientLogo — renders a client brand logo (PNG) at a consistent capped
 * height on the ambient page background. No card, no frame, no border by
 * default — the logo stands on its own.
 *
 * Props:
 *  - logo:     path to the logo image (e.g. '/logos/sephora.png')
 *  - name:     brand name (used for alt + data-testid)
 *  - size:     'xs' | 'sm' | 'md' | 'lg' | 'xl'
 *  - onDark:   only affects the text fallback colour (for the internal
 *              programme which has no logo file). Default false.
 *  - className: extra classes on the outer wrapper
 */
const heightMap = {
  xs: 'h-6 md:h-7',
  sm: 'h-9 md:h-10',
  md: 'h-12 md:h-14',
  lg: 'h-16 md:h-20',
  xl: 'h-20 md:h-24',
};

export default function ClientLogo({
  logo,
  name,
  size = 'md',
  onDark = false,
  className = '',
}) {
  // Fallback for clients without a logo file (e.g. our internal programme).
  if (!logo) {
    const textSize = size === 'xl' ? 'text-[44px]' : size === 'lg' ? 'text-[32px]' : size === 'sm' ? 'text-[18px]' : 'text-[24px]';
    return (
      <div data-testid={`client-logo-${name}`} className={`inline-flex items-center ${className}`}>
        <span className={`serif ${textSize} leading-none tracking-tight`} style={{ color: onDark ? '#FBFAF6' : '#0F3D22' }}>
          {name}
        </span>
      </div>
    );
  }

  return (
    <div
      data-testid={`client-logo-${name}`}
      className={`inline-flex items-center justify-center max-w-full min-w-0 ${className}`}
      title={name}
    >
      <img
        src={logo}
        alt={name}
        loading="lazy"
        decoding="async"
        className={`w-auto max-w-full object-contain object-left select-none pointer-events-none block ${heightMap[size]}`}
      />
    </div>
  );
}
